/**
 * pages/ImportWizard.js
 * One-time catalog import from the company's Excel export. A single page, 5 internal steps
 * (Pilih File -> Pilih Sheet -> Kode Kategori -> Tinjau Kode Pasar -> Ringkasan & Impor), all
 * state local to this component — "Kembali" preserves it, leaving the page (via "Tutup" or
 * after a successful import) discards it entirely. No persistence of wizard state itself; only
 * the final "Impor Sekarang" commit touches `data`.
 *
 * Task B1b — Pilih Sheet (filter-early sheet selection): inserted between Pilih File and Kode
 * Kategori. Every sheet's fitting/not-fitting state is decided ONCE, from the initial no-options
 * parseWorkbook call, using its own skippedSheets output — the exact same test the parser
 * itself applies. Task B1b-fix: `parsed` re-derives LIVE on every checkbox toggle (not only when
 * leaving this step) so step 1's summary and every other screen never show a stale count for a
 * selection the user has already changed. The untouched default (every fitting sheet) restores
 * the STASHED original no-options parse exactly (`initialParsed`, never recomputed) rather than
 * re-running parseWorkbook with an equivalent includeSheets set — this is what keeps the
 * real-file baseline numbers (2351/88/504/125…) byte-identical, not just structurally-equal,
 * on the default path; any other selection re-parses via utils/importParser.js's task-B1a
 * `options.includeSheets`.
 *
 * All heavy lifting already exists and is unit-tested: utils/importParser.js (file -> parsed
 * rows) and utils/importCommit.js (parsed + reviewed decisions -> a ready commit). This page
 * is wiring, tables, and one atomic hand-off to App.js's onCommit.
 */
import React, { useEffect, useMemo, useState } from "react";
import { read, utils as xlsxUtils } from "xlsx";
import { parseWorkbook } from "../utils/importParser";
import { buildPrefixPlan, validatePrefixPlan, applyReviewDecisions, buildCommit, codesForEntry } from "../utils/importCommit";
import { SEED_CODES, isUntouchedSeed, wipedData } from "../utils/importWipe";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import MarketCodeEditor from "../components/MarketCodeEditor";

const STEP_TITLES = ["Pilih File", "Pilih Sheet", "Kode Kategori", "Tinjau Kode Pasar", "Ringkasan & Impor"];

/** Friendly Indonesian phrasing for a parser warning reason (task B1b) — the raw reason string
 *  used to render verbatim ("MYSTERY SHEET baris 12: no-stock"), reading like an internal debug
 *  code rather than something a non-technical user could act on. */
function reasonText(reason) {
  switch (reason) {
    case "no-stock": return "Stok kosong — barang tetap diimpor dengan stok 0.";
    case "no-header": return "Tidak ditemukan baris header — sheet dilewati.";
    case "suspicious-cell": return "Sel berisi tanggal di kolom kode/stok — nilai dianggap 0.";
    default: return reason;
  }
}

export default function ImportWizard({ data, onCommit, onDone }) {
  const [step, setStep] = useState(1);

  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState("");
  const [removeSeed, setRemoveSeed] = useState(false);
  const [wipeAll, setWipeAll] = useState(false);
  const [confirmWipeOpen, setConfirmWipeOpen] = useState(false);

  // Task B1b — filter-early sheet selection. rawSheets: the extracted {name,rows}[] kept around
  // so a toggle can re-parseWorkbook() with a chosen subset. initialParsed: the STASHED original
  // no-options parse result (task B1b-fix) — never overwritten, so returning to the default
  // selection can restore it exactly (byte-identical, not just structurally equal) instead of
  // recomputing an equivalent parse. sheetInventory: built ONCE from that initial parse —
  // fitting/not-fitting + item count per sheet, fixed for the whole wizard session even as
  // `parsed` changes on every toggle. includeSheets: the live checkbox selection (a Set of
  // trimmed+uppercased sheet names), defaulted to every fitting sheet. All four are null until a
  // file is loaded ("not yet chosen").
  const [rawSheets, setRawSheets] = useState(null);
  const [initialParsed, setInitialParsed] = useState(null);
  const [sheetInventory, setSheetInventory] = useState(null);
  const [includeSheets, setIncludeSheets] = useState(null);

  const [plan, setPlan] = useState(null);
  const [overrides, setOverrides] = useState({});
  // Task B2b-ii: which review rows the user has actually looked at (or edited) since their
  // decision became "split" — a nudge, not a gate (see the step-5 warn-only count below). UI
  // state only, never read by the commit layer (importCommit.js has no "reviewed" concept).
  const [reviewedRows, setReviewedRows] = useState(() => new Set());
  const [commitResult, setCommitResult] = useState(null);
  const [committing, setCommitting] = useState(false);

  // Stable for the whole wizard session — the step-5 preview and the real commit must share
  // the exact same date (one shared date for the whole import batch).
  const [commitDate] = useState(() => new Date().toISOString());

  const untouchedSeed = isUntouchedSeed(data);
  const anythingToWipe = data.items.length > 0 || data.recipes.length > 0;

  // Radio-like: checking one option unchecks the other. Seed-strip is the gentle option
  // (7 known sample items only); full wipe is the strong one (everything). Both checkboxes
  // can be visible at once (a fresh, untouched seed app satisfies both conditions), but at
  // most one is ever checked.
  const checkRemoveSeed = (checked) => { setRemoveSeed(checked); if (checked) setWipeAll(false); };
  const checkWipeAll = (checked) => { setWipeAll(checked); if (checked) setRemoveSeed(false); };

  // effectiveData = what buildCommit should treat as "already there". Three cases:
  //   - wipeAll: a fully-empty data object (wipedData) — the import starts from true zero.
  //   - removeSeed: data, minus the 7 seed items + their history. Categories/cars are
  //     deliberately kept (the plan will just reuse SIDE GEAR/SG via buildPrefixPlan, and any
  //     leftover seed cars are harmless/mergeable via the Mobil page).
  //   - neither: data, unchanged (import adds to what's already there).
  const effectiveData = useMemo(() => {
    if (wipeAll) return wipedData(data);
    if (!removeSeed) return data;
    const seedSet = new Set(SEED_CODES);
    return {
      ...data,
      items: data.items.filter((it) => !seedSet.has(it.internalCode)),
      history: data.history.filter((h) => !seedSet.has(h.itemCode)),
    };
  }, [data, removeSeed, wipeAll]);

  // A fresh parse or a flipped checkbox invalidates every downstream step's derived state —
  // plan/overrides/commitResult are all built from parsed+effectiveData and would otherwise
  // silently go stale (e.g. a plan built against the seed-included data used after the user
  // goes back and checks "remove seed"). Task B1b-fix: toggling a sheet in Pilih Sheet now
  // re-derives `parsed` LIVE (see toggleIncludeSheet), so it ALSO goes through this same reset
  // on every toggle, not just when leaving the step — a plan built before a selection change
  // is invalidated the instant the change happens. The plan-rebuild effect just below picks it
  // back up whenever the user actually reaches step 3.
  useEffect(() => {
    setPlan(null);
    setOverrides({});
    setReviewedRows(new Set()); // task B2b-ii: stale review state must not survive a re-parse
    setCommitResult(null);
  }, [parsed, removeSeed, wipeAll]);

  const validation = useMemo(
    () => (plan ? validatePrefixPlan(plan, effectiveData) : { ok: true, errors: [] }),
    [plan, effectiveData]
  );
  const errorByName = useMemo(
    () => new Map(validation.errors.map((e) => [e.name, e.error])),
    [validation]
  );

  // Task B2b-ii: unreviewed split rows — a WARN-ONLY count shown on step 5, never a gate. Same
  // override-normalization as step 4's row map (deliberately not extracted into a shared
  // helper — this one only needs the resulting decision, not codes/customCodes too).
  const unreviewedSplitCount = useMemo(() => {
    if (!parsed) return 0;
    return parsed.reviewList.reduce((count, entry, i) => {
      const ov = overrides[i];
      const isObj = ov != null && typeof ov === "object";
      const decision = (isObj ? ov.decision : ov) || entry.decision;
      return decision === "split" && !reviewedRows.has(i) ? count + 1 : count;
    }, 0);
  }, [parsed, overrides, reviewedRows]);

  // Task B1b: build the prefix plan on step-3 (Kode Kategori) entry, from the freshest `parsed`
  // — moved out of the old step-1-Lanjut handler because `parsed` can now change while the user
  // is still on Pilih Sheet (task B1b-fix: live on every toggle, not just on leaving). This
  // effect needs no change for that: it only fires once `step === 3`, always reading whatever
  // `parsed` currently is at that point, however it got there. Deliberately keyed on `plan`
  // itself (not just `step`) so this survives the reset effect above: if `parsed`/removeSeed/
  // wipeAll flip while already on step 3, that effect nulls `plan` on the SAME render pass this
  // effect also runs on — reading `plan` from that pass would still see the stale non-null value
  // (both effects close over the same pre-commit render), so a naive `[step]`-only dependency
  // would silently never rebuild. Listing `plan` forces one more pass once it actually goes
  // null, guaranteeing the rebuild always fires against the CURRENT parsed/effectiveData rather
  // than reading a stale `parsed` synchronously right after a same-tick setParsed.
  useEffect(() => {
    if (step !== 3 || plan || !parsed) return;
    setPlan(buildPrefixPlan(parsed, effectiveData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, parsed, plan]);

  // Recompute the commit preview only when actually entering step 5 (not on every keystroke
  // elsewhere) — but DOES recompute on every fresh entry, so going back to fix an override or a
  // category code and returning to step 5 always reflects the latest edits.
  useEffect(() => {
    if (step !== 5 || !plan) return;
    const items = applyReviewDecisions(parsed.items, parsed.reviewList, overrides);
    setCommitResult(buildCommit({ ...parsed, items }, plan, effectiveData, { date: commitDate }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setParseError("");
    setParsed(null);
    setRawSheets(null);
    setInitialParsed(null);
    setSheetInventory(null);
    setIncludeSheets(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = read(ev.target.result, { cellDates: true });
        const sheets = wb.SheetNames.map((name) => ({
          name,
          rows: xlsxUtils.sheet_to_json(wb.Sheets[name], { header: 1, raw: true, defval: null }),
        }));
        // The INITIAL parse — no options, exactly the pre-B1b call — is both what `parsed`
        // starts as (preserving the baseline by construction) AND the single source of truth
        // for the sheet inventory below: a sheet is "fitting" iff it did NOT land in this
        // parse's own skippedSheets, the same test parseWorkbook itself already applies. Task
        // B1b-fix: also stashed as `initialParsed` state, untouched for the whole session, so
        // returning the selection to default can restore this EXACT object later rather than
        // recomputing an equivalent (but not reference-identical) parse.
        const freshParsed = parseWorkbook(sheets);
        const skippedKeys = new Set(freshParsed.skippedSheets.map((s) => s.sheet.trim().toUpperCase()));
        const itemCountByCategory = new Map();
        for (const it of freshParsed.items) {
          itemCountByCategory.set(it.category, (itemCountByCategory.get(it.category) || 0) + 1);
        }
        const inventory = sheets.map((s) => {
          const trimmedName = s.name.trim(); // matches importParser's own CATEGORY_FROM_SHEET
          const key = trimmedName.toUpperCase();
          const fitting = !skippedKeys.has(key);
          return { name: s.name, key, fitting, itemCount: fitting ? (itemCountByCategory.get(trimmedName) || 0) : null };
        });
        setRawSheets(sheets);
        setInitialParsed(freshParsed);
        setSheetInventory(inventory);
        setIncludeSheets(new Set(inventory.filter((r) => r.fitting).map((r) => r.key)));
        setParsed(freshParsed);
      } catch {
        setParseError("Gagal membaca file. Pastikan file adalah .xlsx yang valid.");
      }
    };
    reader.onerror = () => setParseError("Gagal membaca file. Pastikan file adalah .xlsx yang valid.");
    reader.readAsArrayBuffer(file);
  };

  // The set of sheet keys the default (untouched) selection represents — every fitting sheet,
  // nothing more. Fixed once sheetInventory is built; used by toggleIncludeSheet below to
  // detect "back to default" vs. "genuinely narrowed."
  const defaultChosenKeys = useMemo(() => {
    if (!sheetInventory) return null;
    return new Set(sheetInventory.filter((r) => r.fitting).map((r) => r.key));
  }, [sheetInventory]);

  const isDefaultSelection = (set) =>
    set.size === defaultChosenKeys.size && [...set].every((k) => defaultChosenKeys.has(k));

  // Task B1b-fix: re-parse LIVE on every toggle, not only when leaving the step — otherwise
  // step 1's summary (and anything else reading `parsed`) shows a stale count until the user
  // advances. Back to exactly the default selection restores the STASHED `initialParsed`
  // object (byte-identical, not a fresh equivalent parse) — this is what guarantees the
  // real-file baseline (2351/88/504…) cannot move for a user who ends up back at default,
  // even after touching checkboxes along the way. Any other selection re-parses filter-early
  // against the raw sheets. The existing reset effect (keyed on `[parsed, ...]`) picks up
  // either case for free — no separate reset logic needed here.
  const toggleIncludeSheet = (key, checked) => {
    setIncludeSheets((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key); else next.delete(key);
      setParsed(isDefaultSelection(next) ? initialParsed : parseWorkbook(rawSheets, { includeSheets: next }));
      return next;
    });
  };

  const updatePlanCode = (i, code) =>
    setPlan((prev) => prev.map((row, idx) => (idx === i ? { ...row, code: code.toUpperCase() } : row)));

  // Task B2b-ii: a hand-edit to a split row's chips. Writes the OBJECT override shape (B2b-i)
  // so importCommit.js's applyReviewDecisions picks these exact codes up instead of recomputing
  // them from entry.raw — editing IS reviewing, so this also marks the row reviewed.
  const setRowCodes = (i, nextCodes) => {
    setOverrides((o) => ({ ...o, [i]: { decision: "split", codes: nextCodes } }));
    setReviewedRows((prev) => { const next = new Set(prev); next.add(i); return next; });
  };

  const runCommit = () => {
    setCommitting(true);
    // Defer so React actually paints the disabled "Mengimpor…" state before the (synchronous,
    // 2000+ item) App-level update runs.
    setTimeout(() => {
      onCommit(commitResult, { removeSeed, wipeAll });
      onDone();
    }, 0);
  };

  // A full wipe is destructive and irreversible — gate it behind a typed confirm. Seed-strip
  // (only 7 known sample items) does not need this extra step.
  const handleImportClick = () => {
    if (wipeAll) { setConfirmWipeOpen(true); return; }
    runCommit();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Impor Data</h1>
          <div className="page-sub">Langkah {step} dari 5 — {STEP_TITLES[step - 1]}</div>
        </div>
        <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
          <button type="button" className="btn" onClick={onDone}>Tutup</button>
        </div>
      </div>

      {step === 1 && (
        <div className="card">
          <div className="form-row">
            <label className="form-label" htmlFor="import-wizard-file">Pilih File Excel (.xlsx)</label>
            <input id="import-wizard-file" type="file" accept=".xlsx" onChange={handleFileChange} />
          </div>

          {parseError && <div className="alert alert--error">{parseError}</div>}

          {parsed && (
            <>
              <div className="page-sub" style={{ marginBottom: "var(--gap-md)" }}>
                {parsed.items.length} barang · {parsed.categories.length} kategori · {parsed.cars.length} mobil
              </div>

              {parsed.skippedSheets.length > 0 && (
                <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
                  Dilewati: {parsed.skippedSheets.length} sheet
                  <ul style={{ margin: "4px 0 0 18px" }}>
                    {parsed.skippedSheets.map((s, i) => (
                      <li key={i}>{s.sheet} ({s.reason === "excluded" ? "dikecualikan" : "tanpa header standar"})</li>
                    ))}
                  </ul>
                </div>
              )}

              {parsed.warnings.length > 0 && (
                <div className="form-hint" style={{ marginBottom: "var(--gap-md)" }}>
                  {parsed.warnings.length} peringatan
                  <ul style={{ margin: "4px 0 0 18px" }}>
                    {parsed.warnings.map((w, i) => (
                      <li key={i}>{w.sheet}{w.rowIndex != null ? ` baris ${w.rowIndex}` : ""}: {reasonText(w.reason)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {data.items.length > 0 && (
                // No alert--warn variant exists in styles.css (only error/ok/info) and this
                // file may not touch styles.css — reusing the same amber inline-style pair
                // DaftarBarang's own low-stock banner already uses, so the tone still reads as
                // "warning" without adding a new class.
                <div style={{
                  background: "#fef3c7", color: "#92400e",
                  padding: "10px 14px", borderRadius: 8, marginBottom: "var(--gap-md)",
                }}>
                  Aplikasi sudah berisi {data.items.length} barang. Impor akan MENAMBAH data, bukan mengganti.
                </div>
              )}

              {untouchedSeed && (
                <label style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)", marginBottom: "var(--gap-md)" }}>
                  <input type="checkbox" checked={removeSeed}
                    onChange={(e) => checkRemoveSeed(e.target.checked)} />
                  Hapus 7 barang contoh dulu (mulai dari kosong)
                </label>
              )}

              {anythingToWipe && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: "var(--gap-sm)", marginBottom: "var(--gap-md)" }}>
                  <input type="checkbox" checked={wipeAll} style={{ marginTop: "var(--gap-xs)" }}
                    onChange={(e) => checkWipeAll(e.target.checked)} />
                  <span>
                    Hapus SEMUA data dulu (mulai benar-benar dari kosong)
                    <div className="form-hint">
                      Menghapus semua barang, riwayat, transformasi, resep, saudara, kategori,
                      mobil, daftar toko/tipe, dan pengaturan.
                    </div>
                  </span>
                </label>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-primary" onClick={() => setStep(2)}>Lanjut</button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 2 && sheetInventory && (
        <div className="card">
          <div className="card__title">Pilih Sheet ({sheetInventory.length} sheet)</div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th></th><th>Sheet</th><th>Keterangan</th></tr></thead>
              <tbody>
                {sheetInventory.map((row) => {
                  const checked = includeSheets.has(row.key);
                  return (
                    <tr key={row.key} style={{ cursor: "default" }}>
                      <td>
                        <input type="checkbox" checked={checked}
                          onChange={(e) => toggleIncludeSheet(row.key, e.target.checked)} />
                      </td>
                      <td>{row.name}</td>
                      <td>
                        {row.fitting
                          ? `${row.itemCount} barang`
                          : (checked
                            ? <span className="form-hint" style={{ color: "var(--amber)" }}>
                                Sheet ini dicentang, tapi formatnya tidak cocok, jadi tetap akan dilewati.
                              </span>
                            : <span className="form-hint">
                                Format kolom berbeda — tidak ada kolom stok (KODE BARANG, SISA, IJM).
                              </span>)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setStep(1)}>Kembali</button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(3)}>Lanjut</button>
          </div>
        </div>
      )}

      {step === 3 && plan && (
        <div className="card">
          <div className="card__title">Kode Kategori ({plan.length} kategori)</div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Kategori</th><th>Kode</th><th>Status</th></tr></thead>
              <tbody>
                {plan.map((row, i) => {
                  const error = errorByName.get(row.name);
                  return (
                    <tr key={`${row.name}-${i}`} style={{ cursor: "default" }}>
                      <td>{row.name}</td>
                      <td>
                        {row.existing
                          ? <span className="mono">{row.code}</span>
                          : (
                            <input className="form-input mono" style={{ width: 100 }} value={row.code}
                              onChange={(e) => updatePlanCode(i, e.target.value)} />
                          )}
                        {error && <div className="form-hint" style={{ color: "var(--red)" }}>{error}</div>}
                      </td>
                      <td>
                        {row.existing
                          ? <span className="chip chip--muted">dipakai</span>
                          : <span className="chip chip--baru">baru</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setStep(2)}>Kembali</button>
            <button type="button" className="btn btn-primary" disabled={!validation.ok} onClick={() => setStep(4)}>
              Lanjut
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="card">
          <div className="card__title">
            {parsed.reviewList.length} sel mengandung "/" — periksa keputusan di bawah
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Sheet</th><th>Sel Asli</th><th>Keputusan</th><th>Kode Hasil</th></tr></thead>
              <tbody>
                {parsed.reviewList.map((entry, i) => {
                  // Task B2b-ii: overrides[i] can now be a bare string (plain Pisah/Gabung flip)
                  // OR an object {decision, codes} (a hand-edit, task B2b-i's shape) — reading it
                  // as a bare string here would treat the object itself as "the decision" (never
                  // === "split"/"kept", silently falling to codesForEntry's "kept" branch and
                  // discarding the edit). Normalize FIRST.
                  const ov = overrides[i];
                  const isObj = ov != null && typeof ov === "object";
                  const decision = (isObj ? ov.decision : ov) || entry.decision;
                  const customCodes = isObj ? ov.codes : null;
                  const codes = codesForEntry(entry.raw, decision, customCodes);
                  const needsReview = decision === "split" && !reviewedRows.has(i);
                  return (
                    <tr key={i} style={{ cursor: "default" }}>
                      <td>{entry.sheet}</td>
                      <td className="mono">{entry.raw}</td>
                      <td>
                        <div className="seg">
                          <button type="button" className={decision === "split" ? "active" : ""}
                            onClick={() => setOverrides((o) => ({ ...o, [i]: "split" }))}>Pisah</button>
                          <button type="button" className={decision === "kept" ? "active" : ""}
                            onClick={() => setOverrides((o) => ({ ...o, [i]: "kept" }))}>Gabung</button>
                        </div>
                        {needsReview && (
                          <div style={{ marginTop: "var(--gap-xs)" }}>
                            <span className="form-hint" style={{ color: "var(--amber)" }}>Belum ditinjau</span>
                            {" "}
                            <button type="button" className="btn btn-sm"
                              onClick={() => setReviewedRows((prev) => { const next = new Set(prev); next.add(i); return next; })}>
                              Sudah benar
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        {decision === "split" ? (
                          <MarketCodeEditor value={codes} onChange={(next) => setRowCodes(i, next)} />
                        ) : codes.map((c, j) => (
                          <span key={j} className="chip chip--muted" style={{ marginRight: "var(--gap-xs)" }}>{c}</span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setStep(3)}>Kembali</button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(5)}>Lanjut</button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="card">
          <div className="card__title">Ringkasan</div>
          {!commitResult ? (
            <div className="form-hint">Menghitung ringkasan…</div>
          ) : (
            <>
              <dl className="detail-grid">
                <dt>Barang baru</dt><dd><b>{commitResult.stats.itemsMinted}</b></dd>
                <dt>Kategori baru</dt><dd><b>{commitResult.stats.categoriesCreated}</b></dd>
                <dt>Kategori dipakai ulang</dt><dd><b>{commitResult.stats.categoriesReused}</b></dd>
                <dt>Mobil baru</dt><dd><b>{commitResult.stats.carsCreated}</b></dd>
                <dt>Baris riwayat</dt><dd><b>{commitResult.stats.historyEntries}</b></dd>
              </dl>
              {removeSeed && (
                <div className="form-hint" style={{ marginTop: "var(--gap-md)" }}>
                  7 barang contoh (SG-00001..SG-00007) akan dihapus sebelum impor.
                </div>
              )}
              {wipeAll && (
                <div className="form-hint" style={{ marginTop: "var(--gap-md)" }}>
                  Semua data lama (barang, riwayat, transformasi, resep, kategori, mobil, dll.)
                  akan dihapus sebelum impor.
                </div>
              )}
              {unreviewedSplitCount > 0 && (
                // Task B2b-ii: WARN only — never disables "Impor Sekarang" below. Same amber
                // inline-style pair this file's step-1 banner already uses.
                <div style={{
                  background: "#fef3c7", color: "#92400e",
                  padding: "10px 14px", borderRadius: 8, marginTop: "var(--gap-md)",
                }}>
                  {unreviewedSplitCount} baris pemisahan belum ditinjau — periksa kembali di
                  Langkah 4 jika perlu.
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => setStep(4)} disabled={committing}>Kembali</button>
                <button type="button" className="btn btn-primary" disabled={committing} onClick={handleImportClick}>
                  {committing ? "Mengimpor…" : "Impor Sekarang"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {confirmWipeOpen && (
        <DeleteConfirmModal
          title="Hapus semua data?"
          message={
            `${data.items.length} barang, ${data.recipes.length} resep, ` +
            `${data.transformations.length} transformasi akan dihapus permanen sebelum impor.`
          }
          requireText="HAPUS"
          onConfirm={() => { setConfirmWipeOpen(false); runCommit(); }}
          onClose={() => setConfirmWipeOpen(false)}
        />
      )}
    </div>
  );
}
